/**
 * EmailPreferences — V2 Premium Experience (V2.0 Phase 5, Cluster A).
 *
 * Route: `/settings/email-preferences`. Lets a member opt in/out of the
 * onboarding drip. Transactional emails (security, receipts) are not
 * governed by this toggle.
 *
 * Voice guardrail: "Beta onboarding emails" → "Early access onboarding emails".
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import AppNav from "@/components/app/AppNav";

export default function EmailPreferences() {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/email-preferences");
        setEnabled(!!data.beta_onboarding_drip);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function save(next) {
    setSaving(true);
    try {
      const { data } = await api.put("/me/email-preferences", { beta_onboarding_drip: next });
      setEnabled(!!data.beta_onboarding_drip);
      toast.success(
        next
          ? "You're opted back in to the onboarding series."
          : `Onboarding series paused — ${data.cancelled_queued || 0} queued email${data.cancelled_queued === 1 ? "" : "s"} cancelled.`
      );
    } catch (err) {
      toast.error(formatApiError(err));
      setEnabled(!next);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="email-preferences-page" className="min-h-screen bg-v2-background text-v2-text">
      <AppNav />
      <main className="max-w-2xl mx-auto px-6 md:px-10 pt-10 pb-24">
        <button
          type="button"
          data-testid="email-preferences-back"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-v2-body-sm text-v2-textMuted hover:text-v2-text transition-colors mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>
        <div className="text-v2-caption uppercase tracking-[0.16em] text-v2-accent mb-2">
          Settings
        </div>
        <h1
          className="text-v2-display-md text-v2-text mb-3"
          style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
        >
          Email preferences
        </h1>
        <p className="text-v2-body-md text-v2-textMuted mb-8 leading-relaxed">
          Manage which informational emails you receive from Informed OS. We honor
          your choice immediately — pending onboarding emails are cancelled the moment you toggle off.
        </p>

        {loading ? (
          <div className="text-v2-body-sm text-v2-textMuted flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-card p-6">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-v2-lg flex items-center justify-center bg-v2-accentSoft flex-shrink-0">
                <Mail className="w-5 h-5 text-v2-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-v2-heading-sm text-v2-text">Early access onboarding emails</div>
                <p className="text-v2-body-sm text-v2-textMuted mt-1 leading-relaxed">
                  Seven short messages from Coach Patrice over 14 days, walking you through the
                  platform. Transactional emails (security, password resets, billing receipts)
                  are <em>not</em> affected by this toggle.
                </p>
              </div>
              <button
                type="button"
                data-testid="email-preferences-toggle"
                onClick={() => save(!enabled)}
                disabled={saving}
                aria-checked={enabled}
                role="switch"
                className={`relative inline-flex w-12 h-7 rounded-full border transition-all disabled:opacity-60 shrink-0 ${
                  enabled
                    ? "bg-v2-accent border-v2-accent"
                    : "bg-v2-surfaceSubtle border-v2-border"
                }`}
              >
                <span
                  className={`absolute top-0.5 w-5 h-5 rounded-full transition-all ${
                    enabled ? "bg-v2-accentInk" : "bg-v2-textSubtle"
                  }`}
                  style={{ left: enabled ? "calc(100% - 1.5rem)" : "0.125rem" }}
                />
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
